/**
 * Wire format for iframe ↔ host postMessage communication.
 *
 * Both the embed loader (`src/embed/`) and the iframe-side bridge
 * (`lib/embed-bridge.ts`) import these types, so the contract is enforced at
 * compile time on both sides.
 *
 * Every message carries a `v` field so the protocol can evolve without
 * breaking already-deployed embed snippets. All messages are tagged with the
 * `reachout:` namespace prefix on `type` so host pages that receive them can
 * ignore unrelated messaging cleanly.
 *
 * ## Version skew is the normal case, not the edge case
 *
 * The loader lives in the school's browser cache and the iframe is always
 * freshly served, so at any moment a v1 `embed.js` may be talking to a v2 app.
 * `SUPPORTED_PROTOCOL_VERSIONS` is therefore a *set*: raising
 * `PROTOCOL_VERSION` must never make the guards start rejecting the previous
 * version, or every cached loader goes silent until its CDN entry expires.
 * Drop a version from the set only once you are certain no loader in the wild
 * still speaks it.
 */

export const PROTOCOL_VERSION = 2;

/**
 * Versions the guards accept. Additive by design — see the note above.
 * v1: initial. v2: adds `reachout:config`.
 */
export const SUPPORTED_PROTOCOL_VERSIONS: ReadonlySet<number> = new Set([1, 2]);

export type ProtocolVersion = 1 | 2;

/**
 * How the iframe is being displayed, from the `?embed=` URL param.
 *
 * `ambient` renders like `inline` but auto-plays a locally simulated greeting
 * and only opens a backend session on the student's first interaction.
 */
export type EmbedMode = 'floating' | 'inline' | 'ambient';

/**
 * How a launcher is drawn. Mirrors Salesforce `ChatWidgetLauncherStyle__c`
 * (lower-cased here, capitalised there).
 *
 * Note this says nothing about *where* the launcher goes — that is decided by
 * the host page's markup. `bare` means draw nothing at all: attach the click
 * to the school's own element, or with no markup render no launcher.
 */
export type LauncherStyle = 'icon' | 'pill' | 'link' | 'bare';

/** Icon glyph name. `none` renders the launcher with wording only. */
export type LauncherIcon = 'chat' | 'heart' | 'lifebuoy' | 'sparkle' | 'shield' | 'none';

/**
 * District presentation config, resolved inside the iframe and handed back to
 * the loader.
 *
 * The loader cannot fetch this itself: `endpoint-status` would need
 * `Access-Control-Allow-Origin: *` for arbitrary school origins, and the API
 * URL lives in `window.__REACHOUT_AI_DEFAULTS__` which is unreadable
 * cross-origin. The iframe already probes it on the chat origin where CORS
 * works, so it reports the result back — one message instead of a new
 * CORS-enabled artifact and CloudFront behavior.
 */
export interface EmbedConfig {
	enabled: boolean;
	features: string[];
	chatMode?: 'ai' | 'connect' | 'both';
	/** ChatWidgetColor__c. */
	accent?: string;
	/** ChatWidgetLabel__c, falling back to ChatWidgetHeading__c. */
	label?: string;
	launcherStyle?: LauncherStyle;
	icon?: LauncherIcon;
	/** ChatWidgetHeight__c — initial height for an in-page mount. */
	height?: number;
	position?: 'bottom-right' | 'bottom-left';
}

/** Messages sent FROM the iframe TO the host page. */
export type IframeToHost =
	| {
			v: ProtocolVersion;
			type: 'reachout:ready';
			/** Chat origin — host should use this for subsequent postMessage targets. */
			chatOrigin: string;
	  }
	| {
			v: ProtocolVersion;
			type: 'reachout:config';
			config: EmbedConfig;
	  }
	| {
			v: ProtocolVersion;
			type: 'reachout:resize';
			/** Full content height in px. Use for in-page mount auto-sizing. */
			height: number;
	  }
	| {
			v: ProtocolVersion;
			type: 'reachout:message-sent';
			/** Timestamp at which the student sent a message. No PII. */
			timestamp: string;
	  }
	| {
			v: ProtocolVersion;
			type: 'reachout:session-ended';
			reason: 'user-closed' | 'agent-ended' | 'timeout';
	  }
	| {
			v: ProtocolVersion;
			/**
			 * The student pressed Escape inside the chat. Keystrokes in a
			 * cross-origin frame never reach the host, so without this the floating
			 * panel could only be closed with the pointer once focus was inside it.
			 */
			type: 'reachout:close-request';
	  };

/** Messages sent FROM the host page TO the iframe. */
export type HostToIframe =
	| {
			v: ProtocolVersion;
			type: 'reachout:open';
	  }
	| {
			v: ProtocolVersion;
			type: 'reachout:close';
	  }
	| {
			v: ProtocolVersion;
			type: 'reachout:navigate';
			/** Target route, e.g. "/chat?mode=reachout-ai". */
			to: string;
	  }
	| {
			v: ProtocolVersion;
			type: 'reachout:set-user';
			/**
			 * Signed Gaggle IdP JWT. When present, the iframe can upgrade from
			 * anonymous to authenticated mode. Verified server-side before trust.
			 */
			gaggleToken: string;
	  }
	| {
			v: ProtocolVersion;
			type: 'reachout:set-theme';
			theme: 'light' | 'dark';
	  };

const IFRAME_TO_HOST_TYPES: ReadonlySet<string> = new Set<IframeToHost['type']>([
	'reachout:ready',
	'reachout:config',
	'reachout:resize',
	'reachout:message-sent',
	'reachout:session-ended',
	'reachout:close-request',
]);

const HOST_TO_IFRAME_TYPES: ReadonlySet<string> = new Set<HostToIframe['type']>([
	'reachout:open',
	'reachout:close',
	'reachout:navigate',
	'reachout:set-user',
	'reachout:set-theme',
]);

/** Shared envelope check: supported version + a `reachout:` string type. */
function isEnvelope(data: unknown): data is { v: number; type: string } {
	if (typeof data !== 'object' || data === null) return false;
	const { v, type } = data as { v?: unknown; type?: unknown };
	return (
		typeof v === 'number' &&
		SUPPORTED_PROTOCOL_VERSIONS.has(v) &&
		typeof type === 'string' &&
		type.startsWith('reachout:')
	);
}

/**
 * The two guards discriminate by *direction*, not just by namespace.
 *
 * Previously `isHostToIframe` delegated to `isIframeToHost`, so the iframe
 * would accept its own outbound message types echoed back at it by a host —
 * a `reachout:ready` from the parent was indistinguishable from a legitimate
 * `reachout:open`. Nothing exploitable followed from it, but a guard that
 * doesn't check the thing its name claims is a trap for the next change.
 */
export function isIframeToHost(data: unknown): data is IframeToHost {
	return isEnvelope(data) && IFRAME_TO_HOST_TYPES.has(data.type);
}

export function isHostToIframe(data: unknown): data is HostToIframe {
	return isEnvelope(data) && HOST_TO_IFRAME_TYPES.has(data.type);
}
